/*
 * Cutiile de sediu de pe harta de inventar, pe "All offices".
 *
 * Fiecare sediu primeste un nod PARINTE in cytoscape (nod compus): un dreptunghi
 * cu margine punctata, cu numele sediului sus, in jurul aparatelor lui. Ce nu e
 * al unui singur sediu - internetul, adresele publice, o poarta presupusa pe
 * care o impart doua sedii - ramane afara, deasupra cutiilor.
 *
 * Harta ramane IN CERC, exact ca pe un singur sediu (asezarea radiala din
 * internet - decizia coordonatoarei, 2 octombrie 2026). Cutiile se pun peste
 * cerc; ca sa nu se amestece sediile pe el, copiii fiecarui nod se ordoneaza
 * dupa sediu (`officeRank`), deci aparatele unui sediu ocupa un arc continuu.
 * Daca doua cutii se suprapun pe cerc, e acceptat.
 *
 * Totul e TypeScript pur, fara panza, ca sa poata fi probat.
 */

type Raw = Record<string, unknown>

/** Id-ul nodului parinte al unui sediu. Prefix pe care serverul nu-l foloseste. */
export const BOX_PREFIX = 'imap-box-'

export const NO_OFFICE = 'No office'

export interface OfficeBox {
  /** Id-ul nodului parinte (`imap-box-<officeId>`). */
  id: string
  /** 0 = aparatele fara sediu. */
  officeId: number
  label: string
  /** Nodurile din cutie, in ordinea din raspuns. */
  members: string[]
}

export interface OfficeGroups {
  boxes: OfficeBox[]
  /** nod -> id-ul cutiei lui. Ce lipseste de aici sta in afara cutiilor. */
  parentOf: Record<string, string>
}

function dataOf(item: Raw): Raw {
  if (item && typeof item.data === 'object' && item.data) {
    return item.data as Raw
  }
  return item
}

function idOf(item: Raw): string {
  return String(item.id ?? dataOf(item).id ?? '')
}

function endsOf(item: Raw): [string, string] {
  const data = dataOf(item)
  return [String(item.source ?? data.source ?? ''), String(item.target ?? data.target ?? '')]
}

/** Un lucru care chiar e al unui sediu: aparat din inventar sau gasit pe retea. */
function isReal(data: Raw): boolean {
  return !!data.deviceId || !!data.foundId
}

/** Ce nu e al niciunui sediu, oricum: internetul, iesirile publice, retelele. */
function isShared(data: Raw): boolean {
  return data.kind === 'internet' || data.kind === 'egress' || data.kind === 'network' || data.public === true
}

function officeIdOf(data: Raw): number | null {
  const n = Number(data.officeId)
  return Number.isFinite(n) && data.officeId !== null && data.officeId !== undefined && data.officeId !== '' ? n : null
}

/**
 * Cine intra in ce cutie.
 *
 *  - aparatul din inventar si cel gasit pe retea: sediul lui (`officeId`), iar
 *    0 / lipsa = "No office";
 *  - ce a dedus harta (comutatorul presupus, AP-ul): sediul scris de server -
 *    cheile lor sunt pe sediu (`office-<id>-sw-…`), deci sunt ale unuia singur;
 *  - poarta votata e cheiata doar dupa IP (`gw-192_168_1_1`): aceeasi adresa
 *    in doua sedii e UN nod pe desen. Daca ce atarna direct de ea e din mai
 *    multe sedii, nu are voie sa stea in cutia unuia - ramane afara. Altfel
 *    sta cu sediul celor de sub ea (sau cu al ei, daca n-are nimic dedesubt);
 *  - internetul si iesirile publice: mereu afara.
 *
 * Intoarce null cand n-ar iesi cel putin doua cutii - pe un singur sediu
 * cutia n-ar spune nimic.
 */
export function groupByOffice(nodes: unknown[], edges: unknown[], names: Record<string, string> = {}): OfficeGroups | null {
  const byId = new Map<string, Raw>()
  for (const n of (nodes as Raw[]) ?? []) {
    byId.set(idOf(n), dataOf(n))
  }

  const kids = new Map<string, string[]>()
  for (const e of (edges as Raw[]) ?? []) {
    const [source, target] = endsOf(e)
    if (!byId.has(source) || !byId.has(target)) continue
    const list = kids.get(source) ?? []
    list.push(target)
    kids.set(source, list)
  }

  const officeOf = new Map<string, number>()
  for (const [id, data] of byId) {
    if (isShared(data) || !isReal(data)) continue
    officeOf.set(id, officeIdOf(data) ?? 0)
  }

  for (const [id, data] of byId) {
    if (isShared(data) || isReal(data)) continue
    const own = officeIdOf(data)
    if (data.kind !== 'gateway') {
      if (own !== null) officeOf.set(id, own)
      continue
    }
    /* Sediile a ce atarna DIRECT de poarta (comutatoarele ei, sau aparatele, cand comutatorul e ascuns). */
    const below = new Set<number>()
    for (const k of kids.get(id) ?? []) {
      const kd = byId.get(k) as Raw
      if (isShared(kd)) continue
      const office = officeIdOf(kd) ?? (isReal(kd) ? 0 : null)
      if (office !== null) below.add(office)
    }
    if (below.size === 1) {
      officeOf.set(id, [...below][0])
    } else if (below.size === 0 && own !== null) {
      officeOf.set(id, own)
    }
    /* Mai multe sedii dedesubt: impartita, ramane afara. */
  }

  const members = new Map<number, string[]>()
  for (const id of byId.keys()) {
    const office = officeOf.get(id)
    if (office === undefined) continue
    const list = members.get(office) ?? []
    list.push(id)
    members.set(office, list)
  }
  if (members.size < 2) {
    return null
  }

  /* Dupa nume; "No office" la urma. */
  const boxes: OfficeBox[] = [...members.entries()]
    .map(([officeId, list]) => ({
      id: BOX_PREFIX + officeId,
      officeId,
      label: officeId === 0 ? NO_OFFICE : names[String(officeId)] || nameFromNodes(list, byId) || 'Office #' + officeId,
      members: list,
    }))
    .sort((a, b) => (a.officeId === 0 ? 1 : b.officeId === 0 ? -1 : a.label.localeCompare(b.label)))

  const parentOf: Record<string, string> = {}
  for (const box of boxes) {
    for (const id of box.members) parentOf[id] = box.id
  }
  return { boxes, parentOf }
}

/**
 * id -> nume, din lista de sedii a ecranului (`ID`/`Nume`) si din nota grafului
 * (`offices`: id/name). Lista ecranului castiga; nota acopera ce lipseste.
 */
export function officeNamesFrom(pageOffices: { ID: number; Nume: string }[], graphOffices: { id: number; name: string }[]): Record<string, string> {
  const names: Record<string, string> = {}
  for (const o of graphOffices) {
    if (o && o.name) names[String(o.id)] = o.name
  }
  for (const o of pageOffices) {
    if (o && o.Nume) names[String(o.ID)] = o.Nume
  }
  return names
}

/** Numele sediului, de pe un aparat din cutie (`officeName` vine pe aparatele din inventar). */
function nameFromNodes(ids: string[], byId: Map<string, Raw>): string {
  for (const id of ids) {
    const name = byId.get(id)?.officeName
    if (typeof name === 'string' && name !== '') return name
  }
  return ''
}

/** Nodurile parinte, gata de dat lui cytoscape. `box: true` le deosebeste de restul. */
export function boxElements(groups: OfficeGroups): { data: Record<string, unknown> }[] {
  return groups.boxes.map((box) => ({ data: { id: box.id, label: box.label, box: true, officeId: box.officeId } }))
}

/** Marginea dinauntrul cutiei (`padding` din stil). */
export const BOX_PADDING = 28

/**
 * Locul fiecarui nod in ordinea sediilor, pentru `radialPositions`: rangul
 * cutiei lui (ordinea din `boxes` - dupa nume, "No office" la urma), sau
 * undefined pentru ce sta afara.
 */
export function officeRank(groups: OfficeGroups): (id: string) => number | undefined {
  const rank = new Map<string, number>()
  groups.boxes.forEach((box, i) => rank.set(box.id, i))
  return (id) => {
    const box = groups.parentOf[id]
    return box === undefined ? undefined : rank.get(box)
  }
}
